import { createTheme } from '@mui/material/styles';
import mediaQuery from 'css-mediaquery';

import { AppViewMode } from 'src/constants';
import { PortalConfig } from 'src/context/portalConfigContext';
import * as Colors from 'src/theme/colors';
import mixins from 'src/theme/mixins';
import { components } from 'src/theme/overrides/v5';
import { shape } from 'src/theme/shape';
import { generateTypographyV5 } from 'src/theme/typography';
import { zIndex } from 'src/theme/zindex';

const ZERO_WIDTH = '0px';

/**
 * MUI v5 theme that mirrors the existing v4 theme from themeContext.ts.
 * Component style overrides are ported from the v4 theme into
 * src/theme/overrides/v5/ and wired through the `components` key below.
 */
export const GetCustomThemeV5 = (
  isMobile: boolean,
  portalConfig: PortalConfig,
) => {
  const { clientAccentColor } = portalConfig.brand;

  const internalUserPrimaryMainColor = Colors.InternalAccent;

  const clientUserPrimaryMainColor =
    clientAccentColor || portalConfig.theme.palette.primary.main;

  const primaryMainColor =
    portalConfig.viewMode === AppViewMode.CLIENT
      ? clientUserPrimaryMainColor
      : internalUserPrimaryMainColor;

  const palette = {
    ...portalConfig.theme.palette,
    primary: {
      ...portalConfig.theme.palette.primary,
      main: primaryMainColor,
    },
  };

  const widthOverride = { width: ZERO_WIDTH };
  const ssrMatchMedia = (query: string) => ({
    matches: mediaQuery.match(query, isMobile ? widthOverride : {}),
  });

  const baseTheme = createTheme({
    palette: {
      ...palette,
      action: {
        active: Colors.GraySmall,
        hover: Colors.BlackHeadings,
      },
      tonalOffset: {
        light: 0.2,
        dark: 0.16,
      },
    },
    mixins,
    typography: generateTypographyV5(portalConfig),
    zIndex,
    shape,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1560,
      },
    },
    components: {
      ...components,
      MuiUseMediaQuery: {
        defaultProps: {
          ssrMatchMedia,
        },
      },
    },
  });
  // Override `down` to match MUI v4 behavior:
  // v4 down('xs') = max-width of the *next* breakpoint (sm), not xs itself.
  // This avoids rewriting 169 usages of `down('xs')` across the codebase.
  const { keys, down } = baseTheme.breakpoints;
  const originalDown = down.bind(baseTheme.breakpoints);
  baseTheme.breakpoints.down = (key: number | (typeof keys)[number]) => {
    if (typeof key === 'number') {
      return originalDown(key);
    }
    const idx = keys.indexOf(key);
    const nextKey = keys[idx + 1];
    return nextKey ? originalDown(nextKey) : originalDown(key);
  };

  // TODO: Once the v5 migration is complete, remove the v4 theme (themeContext.ts)
  // and rename this to the stable theme (drop the V5 suffix from the file and export).
  return baseTheme;
};
